import Dexie, { type Table } from 'dexie';
import type {
  AppSetting,
  CountEvent,
  IntervalCounts,
  Survey,
  SurveyStatus,
  SyncQueueItem,
  VehicleCounts,
} from '@/types/survey';
import { createEmptyCounts } from '@/config/vehicleTypes';
import { findLastUndoableEvent, normalizeCounts, replayCounts, totalCount } from '@/utils/counts';
import { buildIntervals } from '@/utils/intervals';
import { generateQueueId } from '@/utils/ids';

/**
 * Local-first storage. Everything the surveyor does is written here first and is
 * never deleted until the server has confirmed it.
 */
export class TrafficSurveyDatabase extends Dexie {
  surveys!: Table<Survey, string>;
  countEvents!: Table<CountEvent, number>;
  syncQueue!: Table<SyncQueueItem, string>;
  appSettings!: Table<AppSetting, string>;

  constructor() {
    super('traffic-survey');
    this.version(1).stores({
      surveys: 'id, status, surveyDate, createdAt, updatedAt',
      countEvents: '++id, surveyId, [surveyId+id], at',
      syncQueue: 'id, surveyId, action, completed, nextRetryAt, [surveyId+action]',
      appSettings: 'key',
    });
  }
}

export const db = new TrafficSurveyDatabase();

export const APP_VERSION: string = typeof __APP_VERSION__ === 'string' ? __APP_VERSION__ : '0.0.0';

/**
 * Statuses that mean the survey still needs the surveyor to do something.
 *
 * `completed` is included deliberately: a survey that was ended but never
 * submitted is the easiest one to forget about, so the app must offer it again
 * on startup rather than leaving it buried in History.
 */
export const UNFINISHED_STATUSES: SurveyStatus[] = ['draft', 'ready', 'recording', 'completed'];

// ---------------------------------------------------------------------------
// Surveys
// ---------------------------------------------------------------------------

export async function createSurvey(
  survey: Omit<Survey, 'createdAt' | 'updatedAt' | 'appVersion' | 'vehicleCounts'> &
    Partial<Pick<Survey, 'vehicleCounts'>>,
): Promise<Survey> {
  const now = Date.now();
  const record: Survey = {
    ...survey,
    vehicleCounts: survey.vehicleCounts ?? createEmptyCounts(),
    appVersion: APP_VERSION,
    createdAt: now,
    updatedAt: now,
  };
  // `add` (not `put`) so a double-tapped Continue button cannot overwrite an
  // existing survey that shares the same id.
  await db.surveys.add(record);
  return record;
}

export async function getSurvey(id: string): Promise<Survey | undefined> {
  return db.surveys.get(id);
}

export async function updateSurvey(id: string, changes: Partial<Survey>): Promise<void> {
  await db.surveys.update(id, { ...changes, updatedAt: Date.now() });
}

/** The single survey the app should offer to resume on startup, if any. */
export async function findActiveSurvey(): Promise<Survey | undefined> {
  const candidates = await db.surveys.where('status').anyOf(UNFINISHED_STATUSES).toArray();
  if (candidates.length === 0) return undefined;
  // A survey that is actually recording always wins over an untouched draft.
  const recording = candidates.filter((s) => s.status === 'recording');
  const pool = recording.length > 0 ? recording : candidates;
  return pool.sort((a, b) => b.updatedAt - a.updatedAt)[0];
}

export async function listSurveys(limit = 50): Promise<Survey[]> {
  return db.surveys.orderBy('createdAt').reverse().limit(limit).toArray();
}

/**
 * Deletes a survey and its count events. Refuses to delete anything that has not
 * been delivered anywhere, so field data cannot be thrown away by accident.
 */
export async function deleteSurvey(id: string, options: { force?: boolean } = {}): Promise<void> {
  const survey = await db.surveys.get(id);
  if (!survey) return;
  const delivered = survey.databaseSynced || survey.localFileGenerated;
  const isThrowawayDraft = survey.status === 'draft' || survey.status === 'ready';
  if (!options.force && !isThrowawayDraft && !delivered) {
    throw new Error('This survey has not been saved anywhere yet and cannot be discarded.');
  }
  await db.transaction('rw', db.surveys, db.countEvents, db.syncQueue, async () => {
    await db.countEvents.where('surveyId').equals(id).delete();
    await db.syncQueue.where('surveyId').equals(id).delete();
    await db.surveys.delete(id);
  });
}

// ---------------------------------------------------------------------------
// Counting
// ---------------------------------------------------------------------------

/**
 * Records one vehicle tap.
 *
 * The event row is tiny (five short fields), so writing one per tap is cheap —
 * far cheaper than rewriting the whole survey object. The denormalised snapshot
 * on the survey record is refreshed separately by `saveCountsSnapshot`.
 */
export async function recordCount(surveyId: string, vehicleTypeId: string): Promise<number> {
  return db.countEvents.add({
    surveyId,
    vehicleTypeId,
    delta: 1,
    at: Date.now(),
  });
}

/**
 * Reverses the most recent tap that has not already been undone. Returns the
 * vehicle type that was decremented, or null when there was nothing to undo.
 * Counts can never go negative because we only ever compensate an increment
 * that really happened.
 */
export async function undoLastCount(surveyId: string): Promise<string | null> {
  return db.transaction('rw', db.countEvents, async () => {
    const events = await db.countEvents.where('surveyId').equals(surveyId).sortBy('id');
    const target = findLastUndoableEvent(events);
    if (!target || target.id === undefined) return null;
    await db.countEvents.update(target.id, { undone: 1 });
    await db.countEvents.add({
      surveyId,
      vehicleTypeId: target.vehicleTypeId,
      delta: -1,
      at: Date.now(),
      undoOf: target.id,
    });
    return target.vehicleTypeId;
  });
}

/** Rebuilds counts from the event log — the recovery path after a refresh. */
export async function loadCounts(surveyId: string): Promise<VehicleCounts> {
  const events = await db.countEvents.where('surveyId').equals(surveyId).sortBy('id');
  return replayCounts(events);
}

/**
 * The clock-aligned 15 minute breakdown for a survey, rebuilt from the event
 * log. Bounded by the actual recording window so a quiet first or last period
 * is still reported rather than silently dropped.
 */
export async function loadIntervals(surveyId: string): Promise<IntervalCounts[]> {
  const [survey, events] = await Promise.all([
    db.surveys.get(surveyId),
    db.countEvents.where('surveyId').equals(surveyId).sortBy('id'),
  ]);
  return buildIntervals(events, survey?.actualStartTimestamp, survey?.actualEndTimestamp);
}

export async function countEventsFor(surveyId: string): Promise<CountEvent[]> {
  return db.countEvents.where('surveyId').equals(surveyId).sortBy('id');
}

/** Whether Undo would currently do anything. */
export async function hasUndoableCount(surveyId: string): Promise<boolean> {
  const events = await db.countEvents.where('surveyId').equals(surveyId).sortBy('id');
  return findLastUndoableEvent(events) !== null;
}

/** Writes the denormalised snapshot used by the summary, history and export. */
export async function saveCountsSnapshot(surveyId: string, counts: VehicleCounts): Promise<void> {
  await updateSurvey(surveyId, { vehicleCounts: normalizeCounts(counts) });
}

export function surveyTotal(survey: Survey): number {
  return totalCount(survey.vehicleCounts);
}

// ---------------------------------------------------------------------------
// Sync queue
// ---------------------------------------------------------------------------

/**
 * Adds a pending delivery. Idempotent: re-submitting a survey re-arms the
 * existing row instead of creating a second one.
 */
export async function enqueueSync(
  surveyId: string,
  action: SyncQueueItem['action'],
): Promise<SyncQueueItem> {
  const existing = await db.syncQueue.where({ surveyId, action }).first();
  if (existing) {
    const reset: SyncQueueItem = {
      ...existing,
      completed: false,
      attempts: 0,
      nextRetryAt: Date.now(),
      lastError: undefined,
    };
    await db.syncQueue.put(reset);
    return reset;
  }
  const item: SyncQueueItem = {
    id: generateQueueId(),
    surveyId,
    action,
    attempts: 0,
    completed: false,
    nextRetryAt: Date.now(),
    createdAt: Date.now(),
  };
  await db.syncQueue.add(item);
  return item;
}

export async function pendingSyncItems(now = Date.now()): Promise<SyncQueueItem[]> {
  const items = await db.syncQueue.filter((item) => !item.completed).toArray();
  return items
    .filter((item) => (item.nextRetryAt ?? 0) <= now)
    .sort((a, b) => a.createdAt - b.createdAt);
}

export async function allPendingSyncItems(): Promise<SyncQueueItem[]> {
  return db.syncQueue.filter((item) => !item.completed).toArray();
}

export async function updateSyncItem(id: string, changes: Partial<SyncQueueItem>): Promise<void> {
  await db.syncQueue.update(id, changes);
}

// ---------------------------------------------------------------------------
// Settings
// ---------------------------------------------------------------------------

export async function getSetting(key: string): Promise<string | undefined> {
  return (await db.appSettings.get(key))?.value;
}

export async function setSetting(key: string, value: string): Promise<void> {
  await db.appSettings.put({ key, value });
}

export const SETTING_LAST_SURVEYOR = 'lastSurveyorName';
